import React, { useState, useRef, useEffect, FC } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PersonIcon from "@mui/icons-material/Person";
import SettingsIcon from "@mui/icons-material/Settings";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useOnClickOutside } from "src/hooks/useOnClickOutside";
import { numberWithCommas } from "src/utils/helpers";
import cart from '../../assets/images/profile/Partner/cartCon.png';
import headp from '../../assets/images/profile/Partner/adminwhite.png';
import delivery from '../../assets/images/profile/Partner/delivery.png';
import dollar from '../../assets/images/profile/Partner/dollar.png';
import { usePartnersAnalyticsQuery } from "src/store/services/bookingApi";
import { auth } from "src/store/Auth";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

interface Props {
  artisanId: string;
}

const FinancialOverview: FC<Props> = ({ artisanId }) => {
  const [timePeriod, setTimePeriod] = useState("Last 30 Days");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [userDetails, setUserDetails] = useState<{ [key: string]: any }>({});
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { data: userResp, isLoading: isLoadingUser } =
    usePartnersAnalyticsQuery({ artisanId, level: "partner" });

  useEffect(() => {
    const payload: any = userResp?.payload?.data[0];
    if (!payload) return;
    setUserDetails(payload);
    // console.log(payload[0])
    // setPaginationData(userResp?.payload?.pagination);
    // setUserWalletDetails(payload?.wallet || { balance: 0, transactions: [] });
  }, [userResp]);

  useOnClickOutside(dropdownRef, () => setIsDropdownOpen(false));

  // Dummy data - matching the image values
  const analyticsData = {
    totalRevenue: {
      value: userDetails.total_order_amount,
      trend: 15.3,
      icon: dollar,
      bgColor: "bg-blue-50",
      iconBg: "bg-blue-500",
      border: "2px solid #BEDBFF",
    },
    totalOrders: {
      value: userDetails.total_order_count,
      description: "Orders completed",
      icon: cart,
      bgColor: "bg-purple-50",
      iconBg: "bg-purple-500",
      border: "2px solid #E9D4FF",
    },
    anyworkxCommission: {
      value: userDetails.total_company_commission,
      details: ["96% of 12% facilitation fee", "Facilitation fee - ₦23,340"],
      icon: dollar,
      bgColor: "bg-yellow-50",
      iconBg: "bg-yellow-500",
      border: "2px solid #FEE685",
    },
    accountOfficerCommission: {
      value: userDetails.total_account_officer_commission,
      details: ["2% of 12% facilitation fee"],
      icon: headp,
      bgColor: "bg-green-50",
      iconBg: "bg-green-500",
      border: "2px solid #A4F4CF",
    },
    shopFloorManagerCommission: {
      value: userDetails.total_shop_floor_manager_commission,
      details: ["2% of 12% facilitation fee"],
      icon: headp,
      bgColor: "bg-red-50",
      iconBg: "bg-red-500",
      border: "2px solid #FFCCD3",
    },
    deliveryFee: {
      value: userDetails.total_delivery_fee,
      icon: delivery,
      bgColor: "bg-gray-50",
      iconBg: "bg-gray-600",
      border: "2px solid #E8D1E8",
    },
  };

  const timePeriods = [
    "Last 7 Days",
    "Last 30 Days",
    "Last 90 Days",
    "Last Year",
  ];

  // Chart data
  const chartData = {
    labels: ["Week 1", "Week 2", "Week 3", "Week 4"],
    datasets: [
      {
        label: "Revenue",
        data: [10000, 12000, 13000, 14000],
        borderColor: "#3b82f6",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  
  
  
    const { authUser } = auth.use()
    const [showImportant, setShowImportant] = useState(true);
    useEffect(() => {
      if (authUser?.role?.includes("centralPartnerManager")) {
        setShowImportant(false);
      }
    }, [authUser]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        padding: 12,
        titleFont: {
          size: 14,
        },
        bodyFont: {
          size: 12,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value: any) {
            return `$${value.toLocaleString()}`;
          },
          font: {
            size: 11,
          },
          color: "#6b6675",
        },
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
        },
      },
      x: {
        ticks: {
          font: {
            size: 11,
          },
          color: "#6b6675",
        },
        grid: {
          display: false,
        },
      },
    },
  };

  return (
    <div className="space-y-6">
      {/* Financial Overview Section */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-semibold text-[#3a3541] mb-1">
              Financial Overview
            </h2>
            <p className="text-sm text-[#6b6675]">
              Key performance metrics and revenue analytics
            </p>
          </div>
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              {timePeriod}
              <KeyboardArrowDownIcon fontSize="small" />
            </button>
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                {timePeriods.map((period) => (
                  <button
                    key={period}
                    onClick={() => {
                      setTimePeriod(period);
                      setIsDropdownOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                      timePeriod === period
                        ? "bg-purple-50 text-purple-600"
                        : "text-gray-700"
                    }`}
                  >
                    {period}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Metric Cards */}
        <div className={`grid grid-cols-1 ${showImportant ? " md:grid-cols-2 lg:grid-cols-6 " : " md:grid-cols-2 lg:grid-cols-2 "}gap-4 mb-6`}>
          {/* Total Revenue */}
          <div
            className={`${analyticsData.totalRevenue.bgColor} rounded-xl p-5 transition-all duration-200 hover:shadow-md`}
          style={{'border': `${analyticsData.totalRevenue.border}`}}>
            <div className="flex items-center justify-between mb-3">
              <div
                className={`${analyticsData.totalRevenue.iconBg} p-2 rounded-full text-white`}
              >
                <img src={analyticsData.totalRevenue.icon} alt="" />
              </div>
            </div>
            <div className="mb-1">
              <p className="text-xs font-medium text-gray-600 mb-1">
                TOTAL REVENUE
              </p>
              <p className="text-xl font-bold text-[#3a3541]">
                ₦{numberWithCommas(analyticsData.totalRevenue.value)}
              </p>
            </div>
            {/* <div className="flex items-center gap-1 text-green-600 text-xs font-medium">
              <ArrowUpwardIcon fontSize="small" />
              <span>{analyticsData.totalRevenue.trend}%</span>
            </div> */}
          </div>

          {/* Total Orders */}
          <div
            className={`${analyticsData.totalOrders.bgColor} rounded-xl p-5 transition-all duration-200 hover:shadow-md`}
          style={{'border': `${analyticsData.totalOrders.border}`}}>
            <div className="flex items-center justify-between mb-3">
              <div
                className={`${analyticsData.totalOrders.iconBg} p-2 rounded-full text-white`}
              >
                
                <img src={analyticsData.totalOrders.icon} alt="" />
              </div>
            </div>
            <div className="mb-1">
              <p className="text-xs font-medium text-gray-600 mb-1">
                TOTAL ORDERS
              </p>
              <p className="text-xl font-bold text-[#3a3541]">
                {numberWithCommas(analyticsData.totalOrders.value)}
              </p>
            </div>
            <p className="text-xs text-gray-600">
              {analyticsData.totalOrders.description}
            </p>
          </div>

          {/* Anyworkx Commission */}
          <div
            className={`${analyticsData.anyworkxCommission.bgColor} rounded-xl p-5 transition-all duration-200 hover:shadow-md 
${showImportant ? "relative" : "hidden"}`}
          style={{'border': `${analyticsData.anyworkxCommission.border}`}}>
            <div className="flex items-center justify-between mb-3">
              <div
                className={`${analyticsData.anyworkxCommission.iconBg} p-2 rounded-full text-white`}
              >
                <img src={analyticsData.anyworkxCommission.icon} alt="" />
              </div>
            </div>
            <div className="mb-1">
              <p className="text-xs font-medium text-gray-600 mb-1">
                ANYWORKX COMMISSION
              </p>
              <p className="text-xl font-bold text-[#3a3541]">
                ₦{numberWithCommas(analyticsData.anyworkxCommission.value)}
              </p>
            </div>
            {analyticsData.anyworkxCommission.details?.map((detail, index) => (
              <p key={index} className="text-xs text-gray-600">
                {detail}
              </p>
            ))}
          </div>

          {/* Account Officer Commission */}
          <div
            className={`${analyticsData.accountOfficerCommission.bgColor} rounded-xl p-5 transition-all duration-200 hover:shadow-md ${showImportant ? "relative" : "hidden"}`}
          style={{'border': `${analyticsData.accountOfficerCommission.border}`}}>
            <div className="flex items-center justify-between mb-3">
              <div
                className={`${analyticsData.accountOfficerCommission.iconBg} p-2 rounded-full text-white`}
              >
                <img src={analyticsData.accountOfficerCommission.icon} alt="" />
              </div>
            </div>
            <div className="mb-1">
              <p className="text-xs font-medium text-gray-600 mb-1">
                ACCOUNT OFFICER COMMISSION
              </p>
              <p className="text-xl font-bold text-[#3a3541]">
                ₦
                {numberWithCommas(analyticsData.accountOfficerCommission.value)}
              </p>
            </div>
            {analyticsData.accountOfficerCommission.details?.map(
              (detail, index) => (
                <p key={index} className="text-xs text-gray-600">
                  {detail}
                </p>
              ),
            )}
          </div>

          {/* Shop Floor Manager Commission */}
          <div
            className={`${analyticsData.shopFloorManagerCommission.bgColor} rounded-xl p-5 transition-all duration-200 hover:shadow-md ${showImportant ? "relative" : "hidden"}`}
          style={{'border': `${analyticsData.shopFloorManagerCommission.border}`}}>
            <div className="flex items-center justify-between mb-3">
              <div
                className={`${analyticsData.shopFloorManagerCommission.iconBg} p-2 rounded-full text-white`}
              >
                 <img src={analyticsData.shopFloorManagerCommission.icon} alt="" />
              </div>
            </div>
            <div className="mb-1">
              <p className="text-xs font-medium text-gray-600 mb-1">
                SHOP FLOOR MANAGER COMMISSION
              </p>
              <p className="text-xl font-bold text-[#3a3541]">
                ₦
                {numberWithCommas(
                  analyticsData.shopFloorManagerCommission.value,
                )}
              </p>
            </div>
            {analyticsData.shopFloorManagerCommission.details?.map(
              (detail, index) => (
                <p key={index} className="text-xs text-gray-600">
                  {detail}
                </p>
              ),
            )}
          </div>

          {/* Delivery Fee */}
          <div
            className={`${analyticsData.deliveryFee.bgColor} rounded-xl p-5 transition-all duration-200 hover:shadow-md ${showImportant ? "relative" : "hidden"}`}
          style={{'border': `${analyticsData.deliveryFee.border}`}}>
            <div className="flex items-center justify-between mb-3">
              <div
                className={`${analyticsData.deliveryFee.iconBg} p-2 rounded-full text-white`}
              >
                <img src={analyticsData.deliveryFee.icon} alt="" />
              </div>
            </div>
            <div className="mb-1">
              <p className="text-xs font-medium text-gray-600 mb-1">
                DELIVERY FEE
              </p>
              <p className="text-xl font-bold text-[#3a3541]">
                ₦{numberWithCommas(analyticsData.deliveryFee.value)}
              </p>
            </div>
          </div>
        </div>

        {/* Revenue Trend Chart */}
        {/* <div className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-[#3a3541] mb-4">
            Revenue Trend
          </h3>
          <div className="h-64">
            <Line data={chartData} options={chartOptions} />
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default FinancialOverview;
